import { useParams } from "react-router-dom";
import Reveal from "../components/ui/Reveal";
import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { services } from "../data/servicios"
import imghero from "../assets/a14.png"
import { FaWhatsapp } from "react-icons/fa";
import LogoMarquee from "./home/LogoMarquee";
import ScrollToTop from "./ui/ScrollToTop";
import SEO from "./SEO";

const ServiceDetail = () => {
    const ref = useRef(null);
    const { slug } = useParams();
    const allServices = services.flatMap(group => group.items);
    const service = allServices.find(s => s.slug === slug);
    const message = `Hola, estoy interesado en el servicio de ${service.title}. ¿Podrían indicarme requisitos, tiempo y costo aproximado?`;

    // 1. Declaramos los hooks SIEMPRE, independientemente de si existe el servicio o no
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"]
    });

    const yRaw = useTransform(scrollYProgress, [0, 1], [-150, 150]);
    const y = useSpring(yRaw, { stiffness: 100, damping: 30 });

    // 2. Realizamos el early return DESPUÉS de declarar los hooks
    if (!service) {
        return <div className="pt-32 text-center">Servicio no encontrado</div>;
    }
    return (
        <>

            <SEO
                title={`${service.title} | Regulación de Predios`}
                description={service.description}
                canonical={`https://www.planixapp.com/servicios/${service.slug}`}
                schema={service.schema}
                image={imghero}
                url={`https://www.planixapp.com/servicios/${service.slug}`}
                keywords={`${service.title}, saneamiento de predios, SUNARP, Perú, formalización de propiedades`}
            />

            <div className="pt-32">

                <ScrollToTop />

                {/* HERO */}
                <section
                    ref={ref}
                    className="relative min-h-[60vh] md:min-h-[65vh] flex items-center justify-center overflow-hidden bg-white"
                >
                    {/* Imagen + Parallax */}
                    <motion.div style={{ y }} className="absolute inset-0 z-0">
                        {/* Imagen responsive */}
                        <img
                            src={imghero}
                            alt={service.title}
                            className="w-full h-full object-cover object-center md:object-[center_30%]"
                        />

                        {/* Overlay degradado elegante */}
                        <div className="absolute inset-0 bg-gradient-to-b 
      from-white/10 
      via-white/60 
      to-white/85
      z-10"
                        />
                    </motion.div>

                    {/* Contenido */}
                    <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
                        <div className="flex flex-col items-center">
                            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-orange-100 text-[#F26B1D] text-4xl mb-6 shadow-sm">
                                {service.icon}
                            </div>
                            <Reveal>
                                <p className="text-sm uppercase tracking-widest text-orange-500 mb-3">
                                    Servicio especializado
                                </p>
                                <h1 className="text-4xl md:text-6xl font-black leading-tight bg-gradient-to-r from-orange-500 via-orange-600 to-[#1C1C1C] bg-clip-text text-transparent">
                                    {service.title}
                                </h1>
                            </Reveal>
                        </div>
                        <Reveal>
                            <p className="mt-6 text-gray-700 text-lg md:text-xl font-medium max-w-2xl mx-auto leading-relaxed">
                                {service.description}
                            </p>
                        </Reveal>
                        <Reveal>
                            <div className="w-16 h-1 bg-orange-500 mx-auto mt-8 rounded-full" />
                        </Reveal>
                    </div>
                </section>

                {/* BENEFICIOS */}
                <section className="py-24 bg-white">
                    <div className="max-w-5xl mx-auto px-6">
                        <Reveal>
                            <h2 className="text-3xl font-bold text-[#1C1C1C]">
                                Beneficios
                            </h2>
                        </Reveal>
                        <div className="mt-10 grid md:grid-cols-2 gap-6">
                            {service.benefits.map((item, index) => (
                                <Reveal key={index}>
                                    <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 hover:shadow-md transition bg-white">
                                        <div className="text-[#F26B1D] text-xl">✔</div>
                                        <p className="text-gray-700">{item}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* PROCESO */}
                <section className="py-24 bg-gray-50">
                    <div className="max-w-5xl mx-auto px-6">
                        <Reveal>
                            <h2 className="text-3xl font-bold text-[#1C1C1C]">
                                Nuestro Proceso
                            </h2>
                        </Reveal>
                        <div className="mt-10 space-y-6">
                            {service.process.map((step, index) => (
                                <Reveal key={index}>
                                    <div className="relative pl-10">
                                        {/* Línea */}
                                        <div className="absolute left-3 top-0 h-full w-[2px] bg-gray-200"></div>
                                        {/* Punto */}
                                        <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-[#F26B1D] text-white text-sm flex items-center justify-center">
                                            {index + 1}
                                        </div>
                                        <p className="text-gray-700 font-medium">{step}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* REQUISITOS Y ALCANCE */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10">

                        {/* REQUISITOS */}
                        <Reveal>
                            <div className="bg-white p-8 rounded-2xl border shadow-sm">
                                <h3 className="text-2xl font-bold text-[#1C1C1C] mb-6">
                                    📄 Requisitos del trámite
                                </h3>

                                <ul className="space-y-3">
                                    {service.requirements?.map((item, index) => (
                                        <li key={index} className="flex items-start gap-3 text-gray-600">
                                            <span className="text-[#F26B1D] font-bold">•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>

                        {/* ALCANCE */}
                        <Reveal>
                            <div className="bg-white p-8 rounded-2xl border shadow-sm">
                                <h3 className="text-2xl font-bold text-[#1C1C1C] mb-6">
                                    📍 ¿Dónde aplica?
                                </h3>

                                <ul className="space-y-3">
                                    {service.scope?.map((item, index) => (
                                        <li key={index} className="flex items-start gap-3 text-gray-600">
                                            <span className="text-[#F26B1D] font-bold">•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Reveal>

                    </div>
                </section>

                {/* TIEMPO Y PRECIO */}
                <section className="py-16 bg-white border-b">
                    <div className="max-w-4xl mx-auto px-6">

                        <div className="grid md:grid-cols-2 gap-6">

                            {/* TIEMPO */}
                            <Reveal>
                                <div className="p-6 rounded-xl border bg-gray-50 text-center">
                                    <p className="text-sm text-gray-500">Tiempo estimado</p>

                                    <h3 className="mt-2 text-2xl font-bold text-[#1C1C1C]">
                                        {service.duration || "A evaluar"}
                                    </h3>
                                </div>
                            </Reveal>

                            {/* PRECIO */}
                            <Reveal>
                                <div className="p-6 rounded-xl border bg-gray-50 text-center">
                                    <p className="text-sm text-gray-500">Precio referencial</p>
                                    <h3 className="mt-2 text-2xl font-bold text-[#F26B1D]">
                                        {service.price || "Consultar"}
                                    </h3>
                                    <span className="text-sm text-gray-500" >Desde***</span>
                                </div>
                            </Reveal>

                        </div>

                    </div>
                    <p className="mt-3 text-xs text-gray-400 text-center">
                        * Los tiempos y precios pueden variar según el caso.
                    </p>
                </section>

                {/* CTA */}
                <section className="relative z-10 px-6 py-24 bg-white">
                    <div className="max-w-5xl mx-auto bg-[#252525] rounded-3xl p-12 text-center shadow-2xl border border-gray-800 overflow-hidden relative">

                        {/* Decoración sutil de fondo para que no se vea plano */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-[#F26B1D] opacity-10 blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#F26B1D] opacity-5 blur-3xl" />

                        <Reveal>
                            <h2 className="text-4xl font-bold text-white mb-4">
                                ¿Necesitas este servicio?
                            </h2>
                        </Reveal>
                        <Reveal>
                            <p className="text-gray-400 max-w-lg mx-auto mb-8">
                                Contáctanos y te asesoraremos en tu proyecto con la experiencia técnica que necesitas.
                            </p>
                        </Reveal>
                        <Reveal>
                            <motion.a
                                href={`https://wa.me/51983974833?text=${encodeURIComponent(message)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="
    inline-flex items-center justify-center gap-3
    bg-[#F26B1D] hover:bg-[#d95a16]
    text-white 
    px-6 py-3 md:px-10 md:py-4
    rounded-xl font-semibold md:font-bold
    text-sm md:text-base
    w-full sm:w-auto
    transition-all duration-300
    shadow-lg shadow-[#F26B1D]/20 
    hover:shadow-xl hover:-translate-y-1
  "
                            >
                                <motion.span whileHover={{ rotate: 10 }}>
                                    <FaWhatsapp size={20} className="md:w-[22px] md:h-[22px]" />
                                </motion.span>

                                <span className="whitespace-nowrap">
                                    Solicitar asesoría
                                </span>
                            </motion.a>
                        </Reveal>
                    </div>
                </section>

                <section className="py-24 bg-white">
                    <div className="max-w-5xl mx-auto px-6 text-center">

                        <Reveal>
                            <h2 className="text-3xl font-bold text-[#1C1C1C]">
                                ¿Por qué elegirnos?
                            </h2>
                        </Reveal>

                        <div className="mt-10 grid md:grid-cols-3 gap-6">

                            <div className="p-6 border rounded-xl">
                                <h3 className="font-semibold text-[#F26B1D]">Experiencia</h3>
                                <p className="text-gray-600 mt-2">Especialistas en saneamiento predial.</p>
                            </div>

                            <div className="p-6 border rounded-xl">
                                <h3 className="font-semibold text-[#F26B1D]">Seguridad Legal</h3>
                                <p className="text-gray-600 mt-2">Procesos respaldados por normativa vigente.</p>
                            </div>

                            <div className="p-6 border rounded-xl">
                                <h3 className="font-semibold text-[#F26B1D]">Acompañamiento</h3>
                                <p className="text-gray-600 mt-2">Te guiamos en todo el proceso.</p>
                            </div>

                        </div>

                    </div>
                </section>

                <LogoMarquee />

            </div>
        </>
    );
};

export default ServiceDetail;